import React, { useState, useCallback, type FC } from "react";
import {
	Modal,
	Input,
	InputNumber,
	Button,
	Row,
	Col,
	DatePicker,
	Popover,
	Tooltip,
	Form,
	message,
	Drawer,
} from "antd";
import {
	EditOutlined,
	InfoCircleOutlined,
	InfoCircleFilled,
	DeleteOutlined,
	SaveOutlined,
	CloseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import {
	getPagePermissions,
	DISPENSARY_PERMISSION,
} from "../../../security/permissions";
import tooltip from "./tooltip.json";
import tooltipWarehouse from "../../wareHouse/components/tooltip.json";

import {
	ContainerResource,
	deleteContainer,
	getContainerById,
	integrationCodeExists,
	updateContainer,
} from "api/generated";
import { useClient } from "hooks/useClient";
import { handleAxiosError } from "common/helpers/generalUtils";
import useLoggedUser from "hooks/useLoggedUser";
import { ValidateBoleanExists } from "./TankFields";
import { volumeFormatter as numberFormatter} from "services/utils/formatterUtil";

const { TextArea } = Input;
const { confirm } = Modal;

interface Props {
	containerId: string;
	onDeleteContainers: () => void;
}

const ContainerShowInfo: FC<Props> = ({ containerId, onDeleteContainers }) => {
	const [form] = Form.useForm();
	const [container, setContainer] = useState<ContainerResource>();
	const [visible, setVisible] = useState(false);
	const [wasEdited, setWasEdited] = useState(false);
	const [integrationCodeVS, setIntegrationCodeVS] =
		useState<ValidateBoleanExists>();

	const { client } = useClient();
	const { permissions } = useLoggedUser();

	const dispensaryPermissions = getPagePermissions(
		DISPENSARY_PERMISSION,
		permissions
	);

	const showModal = useCallback(() => {
		getContainerById({
			client,
			path: { idContainer: containerId },
		})
			.then(({ data }) => {
				setContainer(data);
				setVisible(true);
				setWasEdited(false);
				setIntegrationCodeVS(undefined);
				if (data) {
					form.setFieldsValue({
						containerTotalCapacity: data.containerTotalCapacity,
						containerMinOperationalCapacity:
							data.containerMinOperationalCapacity,
						containerOperationalCapacity:
							data.containerOperationalCapacity,
						containerUsefulCapacity: data.containerUsefulCapacity,
						containerFundingCapacity: data.containerFundingCapacity,
						containerHeelGasCapacity: data.containerHeelGasCapacity,
						containerCalibrationDate: data.containerCalibrationDate
							? dayjs(data.containerCalibrationDate)
							: null,
						containerDescription: data.containerDescription,
						integrationCode: data.integrationCode,
						meterIdentificationKey: data.meterIdentificationKey,
						measurementUncertainty: data.measurementUncertainty,
					});
				}
			})
			.catch((err) =>
				handleAxiosError(err, "No se pudo obtener el contenedor")
			);
	}, [containerId, client, form]);

	const handleReturn = useCallback(() => {
		setVisible(false);
		setWasEdited(false);
		form.resetFields();
	}, [form]);

	const editFields = useCallback(() => {
		setWasEdited(true);
	}, []);

	const handleDeleteContainer = useCallback(
		(id: string) => {
			deleteContainer({
				client,
				path: { idContainer: id },
			})
				.then(({ data }) => {
					if (data) {
						message.info(data.createdMessage);
					}
				})
				.catch((error) =>
					handleAxiosError(
						error,
						"No se ha podido eliminar el contenedor"
					)
				)
				.finally(() => {
					setVisible(false);
					onDeleteContainers();
				});
		},
		[client, onDeleteContainers]
	);

	const showDeleteConfirm = useCallback(() => {
		if (!container) return;
		confirm({
			title: `¿Está seguro que desea eliminar el contenedor ${container.containerKey}?`,
			content: "Se perderán los datos del medidor permanentemente",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			centered: true,
			onOk: () =>
				new Promise<void>((resolve) => {
					setTimeout(() => {
						handleDeleteContainer(container.idContainer ?? "");
						resolve();
					}, 2000);
				}),
		});
	}, [container, handleDeleteContainer]);

	const editContainer = useCallback(() => {
		if (!container) return;

		form.validateFields().then((values) => {
			updateContainer({
				client,
				body: {
					idContainer: container.idContainer ?? "",
					containerDescription: values.containerDescription,
					containerCalibrationDate:
						values.containerCalibrationDate?.format(
							"YYYY-MM-DD HH:mm:ss"
						) ?? null,
					integrationCode: values.integrationCode,
					containerTotalCapacity: values.containerTotalCapacity,
					containerMinOperationalCapacity:
						values.containerMinOperationalCapacity,
					containerOperationalCapacity:
						values.containerOperationalCapacity,
					containerUsefulCapacity: values.containerUsefulCapacity,
					containerFundingCapacity: values.containerFundingCapacity,
					containerHeelGasCapacity: values.containerHeelGasCapacity,
					containerDuctDiameter: container.containerDuctDiameter,
					containerKey: container.containerKey ?? "",
					containerStatus: container.containerStatus ?? false,
					containerType: container.containerType ?? "",
					meterIdentificationKey: values.meterIdentificationKey,
					measurementUncertainty: values.measurementUncertainty,
				},
			})
				.catch((err) =>
					handleAxiosError(
						err,
						"No se ha podido actualizar el contenedor"
					)
				)
				.finally(() => {
					setVisible(false);
					setWasEdited(false);
				});
		});
	}, [container, client, form]);

	const showEditConfirm = useCallback(() => {
		if (!container) return;

		form.validateFields().then((values) => {
			const integrationChanged =
				container.integrationCode !== values.integrationCode;

			const titleConfirm = integrationChanged
				? `¿Está seguro que desea editar el contenedor ${container.containerKey} y cambiar el código de integración de ${container.integrationCode} a ${values.integrationCode}?`
				: `¿Está seguro que desea editar el contenedor ${container.containerKey}?`;

			const messageConfirm = integrationChanged
				? "Todas las nuevas mediciones serán asociadas a este contenedor y las mediciones previas a este cambio no se moverán ni serán afectadas"
				: "Se perderán los datos del medidor";

			confirm({
				title: titleConfirm,
				content: messageConfirm,
				okText: "Sí",
				okType: "danger",
				cancelText: "No",
				centered: true,
				onOk: () =>
					new Promise<void>((resolve) => {
						setTimeout(() => {
							editContainer();
							resolve();
						}, 2000);
					}),
			});
		});
	}, [container, form, editContainer]);

	const onChangeIntegrationCode = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const integrationCode = e.target.value;
			setIntegrationCodeVS(undefined);

			if (integrationCode === container?.integrationCode) return;

			integrationCodeExists({
				client,
				query: { integrationCode },
			})
				.then(({ data }) => {
					if (data) {
						setIntegrationCodeVS({
							validateStatus: data,
							errorMsg: `El código de integración ${integrationCode} ya existe actualmente.`,
						});
						message.error(
							`El código de integración ${integrationCode} ya existe actualmente.`
						);
					}
					editFields();
				})
				.catch((err) =>
					handleAxiosError(
						err,
						"No se ha podido validar el código de integración"
					)
				);
		},
		[container, client, editFields]
	);

	const infoAddon = (tooltipText: string) => (
		<Popover content={tooltipText} trigger="click">
			<InfoCircleFilled />
		</Popover>
	);

	const drawerFooter = dispensaryPermissions.edit ? (
		<div
			style={{
				display: "flex",
				gap: 12,
				justifyContent: "space-between",
			}}
		>
			<Button
				danger
				icon={<DeleteOutlined />}
				onClick={showDeleteConfirm}
			>
				Eliminar
			</Button>
			<div style={{ display: "flex", gap: 12 }}>
				<Button icon={<CloseOutlined />} onClick={handleReturn}>
					Cancelar
				</Button>
				{wasEdited && (
					<Button
						type="primary"
						icon={<SaveOutlined />}
						onClick={showEditConfirm}
						disabled={integrationCodeVS?.validateStatus}
					>
						Guardar cambios
					</Button>
				)}
			</div>
		</div>
	) : (
		<div style={{ display: "flex", justifyContent: "flex-end" }}>
			<Button icon={<CloseOutlined />} onClick={handleReturn}>
				Cerrar
			</Button>
		</div>
	);

	return (
		<div style={{ display: "inline-block" }}>
			<Popover
				content={
					dispensaryPermissions.edit
						? "Editar contenedor"
						: "Ver información"
				}
			>
				<a
					onClick={showModal}
					style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
				>
					{dispensaryPermissions.edit ? (
						<>
							<EditOutlined style={{ fontSize: 18, color: "#3b82f6" }} />
							Editar
						</>
					) : (
						<>
							<InfoCircleOutlined style={{ fontSize: 18, color: "#3b82f6" }} />
							Ver información
						</>
					)}
				</a>
			</Popover>

			<Drawer
				open={visible}
				title="Información del contenedor"
				onClose={handleReturn}
				width={720}
				placement="right"
				maskClosable={false}
				styles={{
					mask: {
						backdropFilter: 'blur(10px)',
					},
				}}
				footer={drawerFooter}
			>
				<p style={{ color: "#64748b", marginBottom: 16 }}>
					Información detallada del contenedor.
				</p>

				<Form form={form} layout="vertical">
					{/* Tipo de contenedor + Clave de identificación */}
					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							<Form.Item label="Tipo de contenedor">
								<Input
									defaultValue={container?.containerType}
									addonAfter={infoAddon(
										tooltip.tipoContenedor
									)}
									disabled
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Clave de identificación">
								<Input
									defaultValue={container?.containerKey}
									addonAfter={infoAddon(
										tooltip.tanques.claveInstalacion
									)}
									disabled
								/>
							</Form.Item>
						</Col>
					</Row>

					{/* Clave del medidor + Incertidumbre de medición */}
					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							<Tooltip title="Clave de identificación del medidor">
								<Form.Item
									name="meterIdentificationKey"
									label="Clave de identificación del medidor"
								>
									<Input disabled placeholder="SME-TQS-XXX-XXXX" />
								</Form.Item>
							</Tooltip>
						</Col>
						<Col xs={24} md={12}>
							<Tooltip title="Incertidumbre de medición del contenedor">
								<Form.Item
									name="measurementUncertainty"
									label="Incertidumbre de medición"
								>
									<InputNumber
										precision={3}
										formatter={numberFormatter}
										style={{ width: "100%" }}
										disabled={!dispensaryPermissions.edit}
										onChange={editFields}
										placeholder="Ej: 0.001"
									/>
								</Form.Item>
							</Tooltip>
						</Col>
					</Row>

					{/* Capacidad total + Volumen mínimo */}
					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							<Form.Item
								name="containerTotalCapacity"
								label="Capacidad total"
							>
								<InputNumber
									formatter={numberFormatter}
									style={{ width: "100%" }}
									disabled={!dispensaryPermissions.edit}
									onChange={editFields}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								name="containerMinOperationalCapacity"
								label="Volumen mínimo de operación"
							>
								<InputNumber
									formatter={numberFormatter}
									style={{ width: "100%" }}
									disabled={!dispensaryPermissions.edit}
									onChange={editFields}
								/>
							</Form.Item>
						</Col>
					</Row>

					{/* Capacidad operativa + útil */}
					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							<Form.Item
								name="containerOperationalCapacity"
								label="Capacidad operativa"
							>
								<InputNumber
									formatter={numberFormatter}
									style={{ width: "100%" }}
									disabled={!dispensaryPermissions.edit}
									onChange={editFields}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								name="containerUsefulCapacity"
								label="Capacidad útil"
							>
								<InputNumber
									formatter={numberFormatter}
									style={{ width: "100%" }}
									disabled={!dispensaryPermissions.edit}
									onChange={editFields}
								/>
							</Form.Item>
						</Col>
					</Row>

					{/* Capacidad fondaje + gas talón */}
					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							<Form.Item
								name="containerFundingCapacity"
								label="Capacidad fondaje"
							>
								<InputNumber
									formatter={numberFormatter}
									style={{ width: "100%" }}
									disabled={!dispensaryPermissions.edit}
									onChange={editFields}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								name="containerHeelGasCapacity"
								label="Capacidad gas talón"
							>
								<InputNumber
									formatter={numberFormatter}
									style={{ width: "100%" }}
									disabled={!dispensaryPermissions.edit}
									onChange={editFields}
								/>
							</Form.Item>
						</Col>
					</Row>

					{/* Producto + Subproducto (read-only) */}
					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							<Form.Item label="Tipo de producto">
								<Input
									value={
										container
											? `${container.product?.productCode ?? ""} - ${container.product?.productDescription ?? ""}`
											: ""
									}
									addonAfter={infoAddon(
										tooltipWarehouse.tipoProducto
									)}
									disabled
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Tipo de subproducto">
								<Input
									value={
										container
											? `${container.subproduct?.code ?? ""} - ${container.subproduct?.description ?? ""}`
											: ""
									}
									addonAfter={infoAddon(
										tooltipWarehouse.tipoSubProducto
									)}
									disabled
								/>
							</Form.Item>
						</Col>
					</Row>

					{/* Vigencia de calibración */}
					<Row gutter={[16, 0]}>
						<Col xs={24}>
							<Tooltip title={tooltip.tanques.calibracion}>
								<Form.Item
									name="containerCalibrationDate"
									label="Vigencia de calibración"
								>
									<DatePicker
										style={{ width: "100%" }}
										disabled={!dispensaryPermissions.edit}
										format="DD-MM-YYYY"
										onChange={editFields}
									/>
								</Form.Item>
							</Tooltip>
						</Col>
					</Row>

					<Row gutter={[16, 0]}>
						<Col xs={24}>
							<Tooltip title={tooltip.tanques.descripcion}>
								<Form.Item
									name="containerDescription"
									label="Localización y descripción"
									rules={[
										{
											required: true,
											message:
												"Introduce la localización y/o descripción.",
										},
									]}
								>
									<TextArea
										autoSize={{ minRows: 1, maxRows: 6 }}
										disabled={!dispensaryPermissions.edit}
										onChange={editFields}
									/>
								</Form.Item>
							</Tooltip>
						</Col>
					</Row>

					<Row gutter={[16, 0]}>
						<Col xs={24}>
							<Form.Item
								name="integrationCode"
								label="Código de integración"
								validateStatus={
									integrationCodeVS?.validateStatus
										? "error"
										: "success"
								}
								help={integrationCodeVS?.errorMsg}
							>
								<Input
									onChange={onChangeIntegrationCode}
									addonAfter={infoAddon(
										tooltip.codigoIntegracion
									)}
									disabled={!dispensaryPermissions.edit}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Drawer>
		</div>
	);
};

export default ContainerShowInfo;